import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  FaInfoCircle,
  FaTrash,
  FaWhatsapp,
  FaFileAlt,
  FaCheck,
  FaTimes,
  FaClock,
  FaThumbsUp,
} from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { Row, Col, Button, Toast, ToastContainer, Dropdown, Spinner } from "react-bootstrap";
import Loader from "../common/Loader.jsx";

// Context & Permissions
import { useGlobal } from "../../GlobalContext";
import { canEdit, canDelete } from "../common/permissionUtils";
import Back from "../../assets/svg/back.svg";
import "../order/order.css";
import "./invoice.css";
import ProfileIc from "../../assets/png/profile-ic.png";
import Location from "../../assets/svg/location.svg";
import User from "../../assets/svg/user.svg";
import Mail from "../../assets/svg/mail.svg";
import Phone from "../../assets/svg/phone.svg";
import AccountType from "../../assets/svg/account_type.svg";
import Amount from "../../assets/svg/amount_ic1.svg";
import Download from "../../assets/svg/downloadReport.svg";
import { ReactComponent as EyeIc } from "../../assets/svg/eye_ic.svg";
import { ReactComponent as DeleteRow } from "../../assets/svg/delete.svg";
import { getInvoiceDetailsApi, getDownloadInvoicePDFApi, getInvoicePaymentHistoryApi, updateInvoiceStatusApi, getWorkflowApi } from "./invoice-api";
import { downloadBlobFile, sanitizeFileName, sanitizeInput, sanitizeTextarea } from "../common/security-utils";
import { applyStateTaxFlags } from "../common/state-tax";
import DeleteModal from "../common/delete-modal";

// ⓘ icon with a breakdown tooltip rendered in a body portal so it is never
// clipped by the table/card overflow. `rows` is an array of [label, value,
// isTotal?] tuples shown above the icon.
const InfoTooltip = ({ rows }) => {
  const iconRef = useRef(null);
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const openTooltip = () => {
    const rect = iconRef.current?.getBoundingClientRect();
    if (rect) {
      setPos({ top: rect.top - 8, left: rect.right });
      setShow(true);
    }
  };
  const closeTooltip = () => setShow(false);

  return (
    <span
      ref={iconRef}
      className="total-info-wrap"
      onMouseEnter={openTooltip}
      onMouseLeave={closeTooltip}
    >
      <FaInfoCircle className="total-info-icon" size={13} />
      {show &&
        createPortal(
          <span
            className="total-info-tooltip"
            style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
          >
            {rows.map(([label, value, isTotal], i) => (
              <span key={i} className={`tt-row${isTotal ? " tt-total" : ""}`}>
                <span>{label}</span>
                <span>{value}</span>
              </span>
            ))}
          </span>,
          document.body,
        )}
    </span>
  );
};

const InvoiceView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};

  // 🔒 Permissions
  const { rolePermissionList } = useGlobal();
  const hasEditPermission = canEdit(rolePermissionList, "Invoice");
  const hasDeletePermission = canDelete(rolePermissionList, "Invoice");

  const emptyInvoiceData = {
    companyName: "",
    invoiceNo: "",
    phone: "",
    email: "",
    customerGstNo: "",
    currencyName: "",
    exchangeRate: "",
    orgCurrencyName: "",
    orgCurrencySymbol: "",
    customerCurrencyId: null,
    organizationCurrencyId: null,
    billingAddress: "",
    amount: "",
    channel: "",
    serialOrder: "",
    invoiceNumber: "",
    invoiceDate: "",
    dueDate: "",
    invoiceStatus: "",
    shippingAddress: {
      name: "",
      address: "",
    },
    billingAddressFull: {
      name: "",
      address: "",
    },
    organizationDetails: {
      name: "",
      gstNo: "",
      accountName: "",
      bankAccNo: "",
      ifscCode: "",
      upiId: "",
    },
    salesPerson: {
      name: "",
      phone: "",
      email: "",
    },
    products: [],
    subTotal: "0",
    totalDiscount: "0",
    gst: "0",
    igst: "0",
    totalTax: "0",
    adjustments: "0",
    totalAmount: "0",
    paidAmount: "0",
    outstandingAmount: "0",
    termsConditions: "",
    customerNotes: "",
    createdAt: "",
    createdBy: "",
    is_delete: false,
  };
  const [invoiceData, setInvoiceData] = useState(emptyInvoiceData);
  const [productList, setProductList] = useState([]);

  // Delete Invoice
  const [deleteId, setDeleteId] = useState(null);
  const [isOpenDelete, setIsOpenDelete] = useState(false);

  useEffect(() => {
    if (deleteId != null) {
      setIsOpenDelete(true);
    }
  }, [deleteId]);

  const cancelDelete = () => {
    setIsOpenDelete(false);
    setDeleteId(null);
  };

  const onSuccessDelete = (response) => {
    setDeleteId(null);
    setIsOpenDelete(false);
    setSnackbarMessage(response?.message || "Invoice deleted successfully");
    setSnackbarStatus(response?.success ?? false);
    handleSnackbarShow();
    if (response?.success === true) {
      setTimeout(() => {
        navigate("/invoice/list");
      }, 1000);
    }
  };

  const [showPdf, setShowPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);

  // Statuses that block cancellation — money has already been collected.
  const NON_CANCELLABLE_STATUSES = ["Paid", "Partially Paid", "Cancelled"];
  const [loadingStatus, setLoadingStatus] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelRemarks, setCancelRemarks] = useState("");
  const [cancelError, setCancelError] = useState("");
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  // PDF pre-fetched ahead of the Share click so navigator.share() can run
  // synchronously within the click's user-gesture (see prefetchSharePdf).
  const [sharePdfFile, setSharePdfFile] = useState(null);
  const [sharePdfSource, setSharePdfSource] = useState("");

  // Invoice Details / Paid History / Workflow tabs
  const [activeTab, setActiveTab] = useState("Invoice Details");
  const [paidHistory, setPaidHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyDeleteId, setHistoryDeleteId] = useState(null);
  const [isOpenHistoryDelete, setIsOpenHistoryDelete] = useState(false);
  const [workflowList, setWorkflowList] = useState([]);
  const [isWorkflowLoading, setIsWorkflowLoading] = useState(false);

  // Each workflow status gets a distinct, meaningful icon + color so the
  // timeline communicates what actually happened at a glance.
  const workflowIcons = {
    created: <FaFileAlt style={{ color: "#172B4D" }} />,
    open: <FaFileAlt style={{ color: "#172B4D" }} />,
    sent: <FaThumbsUp style={{ color: "#3D4AF5" }} />,
    approved: <FaThumbsUp style={{ color: "#2E7D32" }} />,
    paid: <FaCheck style={{ color: "#2E7D32" }} />,
    "partially paid": <FaCheck style={{ color: "#F59E0B" }} />,
    due: <FaClock style={{ color: "#F59E0B" }} />,
    overdue: <FaClock style={{ color: "#D32F2F" }} />,
    cancelled: <FaTimes style={{ color: "#D32F2F" }} />,
    pending: <FaClock style={{ color: "#B8860B" }} />,
  };

  const fetchWorkflow = async () => {
    if (!isValidId(id)) return;
    setIsWorkflowLoading(true);
    try {
      const res = await getWorkflowApi(id);
      setWorkflowList(res?.data?.workflows || []);
    } catch (err) {
      console.error("Failed to fetch invoice workflow", err);
    } finally {
      setIsWorkflowLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchOrder();
      if (activeTab === "Paid History") {
        fetchPaidHistory();
      } else if (activeTab === "Workflow") {
        fetchWorkflow();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, activeTab]);

  const isValidId = (value) => {
    if (!value) return false;
    return /^[a-zA-Z0-9_-]+$/.test(String(value));
  };

  const fetchPaidHistory = async () => {
    if (!isValidId(id)) return;
    setIsHistoryLoading(true);
    try {
      const response = await getInvoicePaymentHistoryApi(id);
      const data = response?.data ?? response;
      const historyList = data?.history ?? data?.payments ?? data?.data ?? [];
      setPaidHistory(Array.isArray(historyList) ? historyList : []);
    } catch (err) {
      setSnackbarMessage("Failed to fetch payment history");
      setSnackbarStatus(false);
      handleSnackbarShow();
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const cancelHistoryDelete = () => {
    setIsOpenHistoryDelete(false);
    setHistoryDeleteId(null);
  };

  const onSuccessHistoryDelete = (response) => {
    setSnackbarMessage(response?.message ?? "Payment deleted");
    setSnackbarStatus(response?.success ?? false);
    handleSnackbarShow();
    setIsOpenHistoryDelete(false);
    setHistoryDeleteId(null);
    if (response?.success === true) {
      fetchPaidHistory();
      fetchOrder();
    }
  };

  const toNumber = (value) => {
    const parsed = Number.parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  // Decode an HTML entity symbol (e.g. "&#8377;" -> "₹") to a plain character.
  const decodeHtmlEntity = (str) => {
    if (!str) return "";
    const el = document.createElement("textarea");
    el.innerHTML = str;
    return el.value;
  };

  // Currency symbol from the invoice API (falls back to ₹). State drives the
  // JSX re-render; the ref makes the value available synchronously inside
  // mapInvoiceToView (which runs before the state update is applied).
  const [currencySymbol, setCurrencySymbol] = useState("₹");
  const currencySymbolRef = useRef("₹");

  const formatMoney = (value) => {
    const num = toNumber(value);
    return `${currencySymbolRef.current}${num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatPercent = (value) => {
    const num = toNumber(value);
    return `${num}%`;
  };

  const formatAddress = (addressObj) => {
    if (!addressObj) return "";
    const parts = [addressObj.address, addressObj.city?.label, addressObj.state?.label, addressObj.country?.label, addressObj.zipcode];
    return parts.filter(Boolean).join(", ");
  };
  const [isIgst, setIsIgst] = useState(false);
  const [isCgst, setIsCgst] = useState(false);
  const [isSgst, setIsSgst] = useState(false);

  const updateStateTaxFlags = async (stateId) => {
    await applyStateTaxFlags(stateId, {
      setIsIgst,
      setIsCgst,
      setIsSgst,
    });
  };
  const mapInvoiceToView = (apiInvoice) => {
    // Resolve currency symbol first so formatMoney() below uses it.
    const symbol = decodeHtmlEntity(apiInvoice?.currency_symbol) || "₹";
    currencySymbolRef.current = symbol;
    setCurrencySymbol(symbol);

    const items = Array.isArray(apiInvoice?.items) ? apiInvoice.items : [];

    const products = items.map((item) => {
      const taxPercent = toNumber(item.tax_percentage);
      const gstPercent = toNumber(item.cgst_percentage) + toNumber(item.sgst_percentage);
      const igstPercent = toNumber(item.igst_percentage);
      return {
        hsnCode: item.hsn_no ?? "--",
        itemName: item.product?.label ?? item.product ?? item.service_name ?? "--",
        quantity: item.quantity ?? "0",
        uom: item.uom?.label ?? item.uom ?? "--",
        rate: formatMoney(item.unit_price),
        amount: formatMoney(toNumber(item.quantity) * toNumber(item.unit_price)),
        cgst_amount: item.cgst_amount,
        sgst_amount: item.sgst_amount,
        igst_amount: item.igst_amount,
        cgst_percentage: formatPercent(toNumber(item.cgst_percentage)),
        sgst_percentage: formatPercent(toNumber(item.sgst_percentage)),
        igst_percentage: formatPercent(toNumber(item.igst_percentage)),
        // Raw numeric fields for discount-then-tax breakdown calculations.
        qtyNum: toNumber(item.quantity),
        rateNum: toNumber(item.unit_price),
        cgstPct: toNumber(item.cgst_percentage),
        sgstPct: toNumber(item.sgst_percentage),
        igstPct: toNumber(item.igst_percentage),

        totalAmount: formatMoney(item.total_amount),
      };
    });

    const calculatedSubTotal = apiInvoice?.items?.reduce((sum, item) => {
      return sum + (toNumber(item.total_amount) || 0);
    }, 0);

    let discountAmount = 0;
    let discountType = "";
    if (apiInvoice?.discount_type === "Percentage") {
      
      const discountValue = Number(apiInvoice?.discount_percentage ?? 0);
      discountType = discountValue + "%";
      const discountAmountValue = Number(apiInvoice?.discount_amount ?? 0);
      discountAmount = discountAmountValue;
    } else {
      discountType = "Rs.";
      const discountValue = Number(apiInvoice?.discount_amount ?? 0);
      discountAmount = discountValue;
    }

    const tdsPercentage = Number(apiInvoice?.tds_percentage ?? 0);
    const tcsPercentage = Number(apiInvoice?.tcs_percentage ?? 0);
    let tdsAmount = 0;
    let tdsType = "";
    if (tcsPercentage > 0) {
      tdsType = "TSC (" + tcsPercentage + "%)";
      tdsAmount = apiInvoice?.tcs_amount ?? 0;
    } else {
      tdsType = "TDS (" + tdsPercentage + "%)";
      tdsAmount = "- " + (apiInvoice?.tds_amount ?? 0);
    }

    const adjustments = apiInvoice?.adjustment ?? 0;
    const total = apiInvoice?.total_amount ?? 0;
    return {
      companyName: apiInvoice.customer_name ?? "",
      invoiceNo: apiInvoice.invoice_number ?? "",
      phone: apiInvoice.phonenumber ?? "",
      email: apiInvoice.email ?? "",
      customerGstNo: apiInvoice.customer_gstno ?? "",
      currencyName: apiInvoice.bill_currency_unit ?? "",
      exchangeRate: apiInvoice.exchange_rate ?? "",
      // Organization (base) currency — used to detect a currency mismatch
      // against the customer/invoice currency below.
      orgCurrencyName: apiInvoice.org_currency_name ?? "",
      orgCurrencySymbol: decodeHtmlEntity(apiInvoice.org_currency_symbol) || "",
      customerCurrencyId: apiInvoice.customer_currency_id ?? null,
      organizationCurrencyId: apiInvoice.organization_currency_id ?? null,
      ownerName: apiInvoice.owner_name ?? "",
      customerIndustry: apiInvoice.customer_industry ?? "",
      creditAmount: formatMoney(apiInvoice.credit_amount),
      customerLocation: apiInvoice.customer_location ?? "",
      customerAddress: formatAddress(apiInvoice.bill_address),
      billingAddress: formatAddress(apiInvoice.bill_address),
      amount: formatMoney(apiInvoice.total_amount),

      serialOrder: apiInvoice.order_no ?? "",
      orderId: apiInvoice.order_id ?? "",
      invoiceNumber: apiInvoice.invoice_number ?? "",
      invoiceDate: apiInvoice.invoice_date ?? "",
      dueDate: apiInvoice.due_date ?? "",
      invoiceStatus: apiInvoice.status ?? "",
      shippingAddress: {
        name: apiInvoice.customer_name ?? "",
        address: formatAddress(apiInvoice.ship_address),
      },
      billingAddressFull: {
        name: apiInvoice.customer_name ?? "",
        address: formatAddress(apiInvoice.bill_address),
      },
      organizationDetails: {
        name: apiInvoice.org_details?.org_name ?? "",
        gstNo: apiInvoice.org_details?.gst_number ?? "",
        accountName: apiInvoice.org_details?.account_name ?? "",
        bankAccNo: apiInvoice.org_details?.account_number ?? "",
        ifscCode: apiInvoice.org_details?.ifsc_code ?? "",
        swiftCode: apiInvoice.org_details?.swift_code ?? "",
        upiId: apiInvoice.org_details?.upi_id ?? "",
        upiIdQr: apiInvoice.org_details?.upi_id_qr ?? "",
      },
      salesPerson: {
        name: apiInvoice.sales_person_name ?? "",
        phone: apiInvoice.sales_person_phone ?? "",
        email: apiInvoice.sales_person_email ?? "",
        profile: apiInvoice.sales_person_profile ?? "",
      },
      products,
      calculatedSubTotal: calculatedSubTotal,
      subTotal: apiInvoice.sub_total ?? "0",
      discountType: discountType,
      discountAmount: discountAmount,
      tdsType: tdsType,
      tdsAmount: tdsAmount,
      adjustments: adjustments,
      // Raw numerics for discount-then-tax calculations.
      rawDiscountType: apiInvoice?.discount_type ?? "",
      rawDiscountPercent: toNumber(apiInvoice?.discount_percentage),
      rawDiscountAmount: toNumber(apiInvoice?.discount_amount),
      rawTdsPercent: tdsPercentage,
      rawTcsPercent: tcsPercentage,
      rawTdsAmount: toNumber(apiInvoice?.tds_amount),
      rawTcsAmount: toNumber(apiInvoice?.tcs_amount),
      rawAdjustment: toNumber(apiInvoice?.adjustment),
      // Additional charges / discounts (line-item style add-ons)
      charges: Array.isArray(apiInvoice?.charges)
        ? apiInvoice.charges.map((c) => ({
            name: c.name ?? "",
            amount: toNumber(c.amount),
            percentage: toNumber(c.percentage),
          }))
        : [],
      additionalDiscounts: Array.isArray(apiInvoice?.discounts)
        ? apiInvoice.discounts.map((d) => ({
            name: d.name ?? "",
            amount: toNumber(d.amount),
            percentage: toNumber(d.percentage),
          }))
        : [],
      totalAmount: total,
      paidAmount: apiInvoice?.paid_amount ?? 0,
      outstandingAmount: apiInvoice?.outstanding_amount ?? 0,
      termsConditions: apiInvoice.terms ?? "",
      customerNotes: apiInvoice.notes ?? "",
      createdAt: apiInvoice.created ?? "",
      createdBy: apiInvoice.created_by ?? "",
      is_delete: apiInvoice?.is_delete === true || apiInvoice?.is_delete === 1 || Boolean(apiInvoice?.is_delete),
    };
  };

  const fetchOrder = async () => {
    setIsLoading(true);
    try {
      const response = await getInvoiceDetailsApi(id);
      const data = response?.data ?? response;
      const apiInvoice = data?.invoice ?? data ?? {};
      const mapped = mapInvoiceToView(apiInvoice);
      setInvoiceData(mapped);
      setProductList(mapped.products);

      if (apiInvoice.ship_address?.state?.value) {
        updateStateTaxFlags(apiInvoice.ship_address.state.value);
      }
    } catch (err) {
      setSnackbarMessage("Failed to fetch invoice details");
      setSnackbarStatus(false);
      handleSnackbarShow();
    } finally {
      setIsLoading(false);
    }
  };

  const extractErrorMessage = (resOrErr, fallback = "Failed to update invoice status") => {
    const res = resOrErr?.response || resOrErr;
    const data = res?.data;
    if (data) {
      if (data.error) {
        if (typeof data.error === "string" && data.error.trim()) return data.error;
        if (Array.isArray(data.error) && data.error.length > 0) return data.error.filter(Boolean).join(". ");
        if (typeof data.error === "object") {
          const vals = Object.values(data.error).flat().filter(Boolean);
          if (vals.length > 0) return vals.join(". ");
        }
      }
      if (data.message && typeof data.message === "string" && data.message.trim() && data.message !== "Validation Error") {
        return data.message;
      }
    }
    return resOrErr?.message || fallback;
  };

  const updateStatus = async (value, remarks) => {
    if (!isValidId(id)) return { success: false, message: "Invalid invoice ID" };

    setLoadingStatus(value);
    try {
      const bodyData = { status: sanitizeInput(value) };
      if (remarks) bodyData.remarks = sanitizeTextarea(remarks);
      const response = await updateInvoiceStatusApi(bodyData, id);

      if (response?.data?.success === false || (response?.status && response.status >= 400)) {
        const errorMsg = extractErrorMessage(response, "Failed to update invoice status");
        setSnackbarMessage(sanitizeInput(errorMsg));
        setSnackbarStatus(false);
        handleSnackbarShow();
        return { success: false, message: errorMsg };
      }

      setSnackbarMessage(sanitizeInput(response?.data?.message ?? "Invoice status updated successfully"));
      setSnackbarStatus(response?.data?.success ?? true);
      handleSnackbarShow();

      fetchOrder();
      fetchWorkflow();
      return { success: true, message: response?.data?.message };
    } catch (err) {
      const errorMsg = extractErrorMessage(err, "Failed to update invoice status");
      setSnackbarMessage(sanitizeInput(errorMsg));
      setSnackbarStatus(false);
      handleSnackbarShow();
      return { success: false, message: errorMsg };
    } finally {
      setLoadingStatus(null);
    }
  };

  const handleCancelConfirm = async () => {
    setCancelError("");
    const result = await updateStatus("Cancelled", cancelRemarks);
    if (result?.success) {
      setShowCancelConfirm(false);
      setCancelRemarks("");
      setCancelError("");
    } else {
      setCancelError(result?.message || "Failed to cancel invoice.");
    }
  };

  const getPdfSource = (payload) => {
    const rawUrl = payload?.url ? String(payload.url).trim() : "";
    if (rawUrl) return rawUrl;
    const rawBase64 = payload?.base64 ? String(payload.base64).trim() : "";
    if (!rawBase64) return "";
    return rawBase64.startsWith("data:") ? rawBase64 : `data:application/pdf;base64,${rawBase64}`;
  };

  const getPdfFileName = () => {
    const invoiceNo = sanitizeInput(invoiceData.invoiceNumber || "invoice");
    return sanitizeFileName(`${invoiceNo}.pdf`);
  };

  // Converts a "data:<mime>;base64,<data>" URI into a Blob — needed because
  // fetch() can't be used on data URIs the way it can on http(s) PDF links.
  const dataUriToBlob = (dataUri) => {
    const [meta, base64] = dataUri.split(",");
    const mimeMatch = meta.match(/data:(.*);base64/);
    const mime = mimeMatch ? mimeMatch[1] : "application/pdf";
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mime });
  };

  // Builds the shared message body — used both in the native file-share
  // caption and the wa.me text fallback, so the content stays in sync. The
  // PDF link/text is always included since some share targets (e.g. Teams)
  // silently drop the `files` payload from navigator.share().
  const buildInvoiceShareText = (pdfSource) => {
    const customerName = invoiceData.companyName || "Customer";
    const lines = [
      `Hello ${customerName},`,
      `Sharing the invoice details for Invoice ${invoiceData.invoiceNo || ""}.`,
      "",
      `Invoice No: ${invoiceData.invoiceNo || ""}`,
      invoiceData.amount ? `Amount: ${invoiceData.amount}` : "",
      invoiceData.dueDate ? `Due Date: ${invoiceData.dueDate}` : "",
      "",
      "Kindly review and confirm so we can proceed with the next steps.",
    ];
    if (pdfSource && pdfSource.startsWith("http")) {
      lines.push("", pdfSource);
    }
    return lines.filter((line) => line !== undefined && line !== null).join("\n");
  };

  const shareWhatsappLink = (pdfSource) => {
    const text = encodeURIComponent(buildInvoiceShareText(pdfSource));
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
  };

  // Fetches the invoice PDF ahead of time and caches it as a File, so the
  // click handler can call navigator.share() synchronously. navigator.share()
  // must run inside the original user-gesture call stack — any `await`
  // before it (e.g. fetching the PDF) makes the browser reject it with
  // NotAllowedError, since by the time the promise resolves the browser no
  // longer considers the click "in progress".
  const prefetchSharePdf = useCallback(async () => {
    if (!id) return;
    try {
      const response = await getDownloadInvoicePDFApi(id);
      const data = response?.data ?? response;
      if (data?.success !== true) return;

      const source = getPdfSource(data);
      if (!source) return;

      setSharePdfSource(source);
      const blob = source.startsWith("data:")
        ? dataUriToBlob(source)
        : await (await fetch(source, { method: "GET", headers: { Accept: "*/*" } })).blob();
      const file = new File([blob], getPdfFileName(), { type: blob.type || "application/pdf" });
      setSharePdfFile(file);
    } catch (error) {
      // Sharing still works via the wa.me link fallback even if this fails.
      console.error("Failed to pre-fetch invoice PDF for sharing:", error);
    }
    // getPdfFileName derives only from invoiceData.invoiceNumber, already
    // effectively covered by the id/invoiceData.invoiceNo dependency below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (invoiceData.invoiceNo) {
      prefetchSharePdf();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceData.invoiceNo]);

  // Shares the invoice. Prefers the native Web Share API with the actual PDF
  // file attached (so it lands in WhatsApp as a real document) — only
  // supported on browsers that implement navigator.canShare({ files }), and
  // only when the PDF was already pre-fetched (see prefetchSharePdf above).
  // Falls back to the wa.me text+link share otherwise.
  const handleShareWhatsapp = () => {
    if (isSharing) return;

    const canTryFileShare =
      sharePdfFile &&
      typeof navigator !== "undefined" &&
      typeof navigator.share === "function" &&
      typeof navigator.canShare === "function" &&
      navigator.canShare({ files: [sharePdfFile] });

    if (canTryFileShare) {
      const shareText = buildInvoiceShareText(sharePdfSource);
      navigator
        .share({ files: [sharePdfFile], text: shareText, title: sharePdfFile.name })
        .catch((error) => {
          if (error?.name === "AbortError") return;
          console.error("Native file share failed, falling back to WhatsApp link:", error);
          shareWhatsappLink(sharePdfSource);
        });
      return;
    }

    if (sharePdfSource) {
      shareWhatsappLink(sharePdfSource);
      return;
    }

    // PDF hasn't finished pre-fetching yet (or fetch failed) — fetch once
    // more just for the link fallback so Share still works, even if slower.
    setIsSharing(true);
    getDownloadInvoicePDFApi(id)
      .then((response) => {
        const data = response?.data ?? response;
        const source = data?.success === true ? getPdfSource(data) : "";
        if (source) {
          setSharePdfSource(source);
          shareWhatsappLink(source);
        } else {
          setSnackbarMessage("No PDF available to share.");
          setSnackbarStatus(false);
          handleSnackbarShow();
        }
      })
      .catch(() => {
        setSnackbarMessage("Failed to prepare invoice for sharing.");
        setSnackbarStatus(false);
        handleSnackbarShow();
      })
      .finally(() => setIsSharing(false));
  };

  const handleDownloadPdf = async (format = 1) => {
    if (!id || isPdfLoading) return;
    setIsPdfLoading(true);
    try {
      const response = await getDownloadInvoicePDFApi(id, format);
      const data = response?.data ?? response;
      if (data?.success !== true) {
        setSnackbarMessage(sanitizeInput(data?.message || "Download failed"));
        setSnackbarStatus(false);
        handleSnackbarShow();
        return;
      }

      const source = getPdfSource(data);
      if (!source) {
        setSnackbarMessage("No PDF available for download.");
        setSnackbarStatus(false);
        handleSnackbarShow();
        return;
      }

      const fileName = getPdfFileName();
      const ok = await downloadBlobFile(source, fileName);
      if (!ok) {
        setSnackbarMessage("Failed to download invoice PDF.");
        setSnackbarStatus(false);
        handleSnackbarShow();
        return;
      }

      setSnackbarMessage(sanitizeInput(data?.message || "Download started"));
      setSnackbarStatus(true);
      handleSnackbarShow();
    } catch (err) {
      setSnackbarMessage("Failed to download invoice PDF.");
      setSnackbarStatus(false);
      handleSnackbarShow();
    } finally {
      setIsPdfLoading(false);
    }
  };

  const handlePreviewPdf = async () => {
    if (!id || isPdfLoading) return;
    if (pdfUrl) {
      setShowPdf(true);
      return;
    }
    setIsPdfLoading(true);
    try {
      const response = await getDownloadInvoicePDFApi(id);
      const data = response?.data ?? response;
      if (data?.success !== true) {
        setSnackbarMessage(sanitizeInput(data?.message || "Preview failed"));
        setSnackbarStatus(false);
        handleSnackbarShow();
        return;
      }

      const source = getPdfSource(data);
      if (!source) {
        setSnackbarMessage("No PDF available for preview.");
        setSnackbarStatus(false);
        handleSnackbarShow();
        return;
      }

      setPdfUrl(source);
      setShowPdf(true);
    } catch (err) {
      setSnackbarMessage("Failed to load invoice PDF preview.");
      setSnackbarStatus(false);
      handleSnackbarShow();
    } finally {
      setIsPdfLoading(false);
    }
  };

  const money = (n) =>
    `${currencySymbol}${toNumber(n).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  // Pre-tax subtotal from the mapped products (quantity × unit price).
  const preTaxSubTotal = (invoiceData.products || []).reduce(
    (sum, p) => sum + toNumber(p.qtyNum) * toNumber(p.rateNum),
    0,
  );

  // Resolve total discount (Rs. directly, or % of pre-tax subtotal), split
  // equally across items — same rule as quotation-view. Tax is charged on the
  // post-discount amount.
  const resolvedDiscount =
    invoiceData.rawDiscountType === "Percentage"
      ? (preTaxSubTotal * toNumber(invoiceData.rawDiscountPercent)) / 100
      : toNumber(invoiceData.rawDiscountAmount);
  const hasDiscount = resolvedDiscount > 0;
  console.log("[discount debug]", {
    rawDiscountType: invoiceData.rawDiscountType,
    rawDiscountPercent: invoiceData.rawDiscountPercent,
    rawDiscountAmount: invoiceData.rawDiscountAmount,
    preTaxSubTotal,
    resolvedDiscount,
    hasDiscount,
    productsLen: (invoiceData.products || []).length,
  });
  const productCount = (invoiceData.products || []).length;
  const perItemDiscount =
    hasDiscount && productCount > 0 ? resolvedDiscount / productCount : 0;

  const getItemBreakdown = (product) => {
    const baseAmount = toNumber(product.qtyNum) * toNumber(product.rateNum);
    const taxableAmount = Math.max(baseAmount - perItemDiscount, 0);
    const cgst = toNumber(product.cgstPct);
    const sgst = toNumber(product.sgstPct);
    const igst = toNumber(product.igstPct);
    const cgstAmount = taxableAmount * (cgst / 100);
    const sgstAmount = taxableAmount * (sgst / 100);
    const igstAmount = taxableAmount * (igst / 100);
    const taxAmount = cgstAmount + sgstAmount + igstAmount;
    return {
      baseAmount,
      itemDiscount: perItemDiscount,
      taxableAmount,
      cgst,
      sgst,
      igst,
      cgstAmount,
      sgstAmount,
      igstAmount,
      taxAmount,
      total: taxableAmount + taxAmount,
    };
  };

  // Aggregate GST across products (on the post-discount base). taxableBase is
  // the sum of taxable amounts of products that carry a GST rate, so the GST
  // tooltip's "On amount" reconciles with the tax shown.
  const gstTotals = (invoiceData.products || []).reduce(
    (acc, p) => {
      const bd = getItemBreakdown(p);
      acc.cgst += bd.cgstAmount;
      acc.sgst += bd.sgstAmount;
      acc.igst += bd.igstAmount;
      if (bd.cgst > 0 || bd.sgst > 0 || bd.igst > 0) {
        acc.taxableBase += bd.taxableAmount;
      }
      return acc;
    },
    { cgst: 0, sgst: 0, igst: 0, taxableBase: 0 },
  );
  const totalGst = gstTotals.cgst + gstTotals.sgst + gstTotals.igst;

  const SubTotals = ({ data = {} }) => {
    return (
      <div className="sub-totals totals-card-inline">
        <div className="d-flex justify-content-between">
          <div className="sub-title">Sub Total</div>
          <div className="order-sub-title-value">{currencySymbol} {data.subTotal}</div>
        </div>
        <hr />
         {/* Additional charges */}
        {(data.charges || []).map((charge, idx) => (
          <div className="d-flex justify-content-between" key={`charge-${idx}`}>
            <div className="sub-discount-label">
              {charge.name || "Charge"}
              {charge.percentage ? ` (${charge.percentage}%)` : ""}
            </div>
            <div className="sub-title">{currencySymbol} {charge.amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        ))}
        {/* Additional discounts */}
        {(data.additionalDiscounts || []).map((discount, idx) => (
          <div className="d-flex justify-content-between" key={`discount-${idx}`}>
            <div className="sub-discount-label">
              {discount.name || "Discount"}
              {discount.percentage ? ` (${discount.percentage}%)` : ""}
            </div>
            <div className="sub-title">{currencySymbol} -{discount.amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        ))}
        <div className="d-flex justify-content-between">
          <div className="sub-discount-label d-inline-flex align-items-center gap-1">
            Discount ({data.discountType})
            {hasDiscount && (
              <InfoTooltip
                rows={[
                  ["On amount", money(preTaxSubTotal)],
                  [
                    "Entered",
                    data.rawDiscountType === "Percentage"
                      ? `${data.rawDiscountPercent}%`
                      : money(data.rawDiscountAmount),
                  ],
                  ["Discount amount", `- ${money(resolvedDiscount)}`],
                  ...(productCount > 0
                    ? [[`Per item (÷${productCount})`, `- ${money(perItemDiscount)}`]]
                    : []),
                ]}
              />
            )}
          </div>
          <div className="sub-title">{currencySymbol} -{data.discountAmount}</div>
        </div>
        {totalGst > 0 && (
          <div className="d-flex justify-content-between">
            <div className="sub-discount-label d-inline-flex align-items-center gap-1">
              GST
              <InfoTooltip
                rows={[
                  ["On amount", money(gstTotals.taxableBase)],
                  ...(gstTotals.cgst > 0 ? [["CGST", money(gstTotals.cgst)]] : []),
                  ...(gstTotals.sgst > 0 ? [["SGST", money(gstTotals.sgst)]] : []),
                  ...(gstTotals.igst > 0 ? [["IGST", money(gstTotals.igst)]] : []),
                  ["Total GST", money(totalGst), true],
                ]}
              />
            </div>
            <div className="sub-title">{money(totalGst)}</div>
          </div>
        )}
        <div className="d-flex justify-content-between">
          <div className="sub-discount-label d-inline-flex align-items-center gap-1">
            Adjustments
            <InfoTooltip
              rows={[
                [
                  "Adjustment",
                  `${toNumber(data.rawAdjustment) < 0 ? "- " : ""}${money(Math.abs(toNumber(data.rawAdjustment)))}`,
                ],
                ["Applied to", "Grand total"],
              ]}
            />
          </div>
          <div className="sub-title">{currencySymbol} {data.adjustments}</div>
        </div>

        <hr />
        <div className="d-flex justify-content-between fw-700">
          <div className="sub-title">Total Amount</div>
          <div className="order-sub-title-value">{currencySymbol} {data.totalAmount}</div>
        </div>

        {/* TDS / TCS — shown below the Total, not included in it */}
        {(toNumber(data.rawTdsAmount) > 0 || toNumber(data.rawTcsAmount) > 0) && (
          <div className="d-flex justify-content-between mt-2">
            <div className="sub-discount-label d-inline-flex align-items-center gap-1">
              {data.tdsType}
              <InfoTooltip
                rows={[
                  ["On amount", money(toNumber(data.totalAmount))],
                  [
                    "Rate",
                    `${data.rawTcsPercent > 0 ? data.rawTcsPercent : data.rawTdsPercent}%`,
                  ],
                  [
                    data.rawTcsPercent > 0 ? "TCS" : "TDS",
                    `${data.rawTcsPercent > 0 ? "+ " : "- "}${money(data.rawTcsPercent > 0 ? data.rawTcsAmount : data.rawTdsAmount)}`,
                    true,
                  ],
                ]}
              />
            </div>
            <div className="sub-title">{currencySymbol} {data.tdsAmount}</div>
          </div>
        )}

        {/* Organization currency differs from the invoice/customer currency —
            show the base-currency equivalent (Total Amount × Exchange Rate). */}
        {data.organizationCurrencyId &&
          data.customerCurrencyId &&
          data.organizationCurrencyId !== data.customerCurrencyId && (
            <div className="d-flex justify-content-between">
              <div className="sub-discount-label d-inline-flex align-items-center gap-1">
                Total in {data.orgCurrencyName || "Org Currency"}
                <InfoTooltip
                  rows={[
                    ["Total Amount", money(toNumber(data.totalAmount))],
                    ["Exchange Rate", `× ${toNumber(data.exchangeRate)}`],
                    [
                      "Converted Amount",
                      `${data.orgCurrencySymbol || ""} ${(toNumber(data.totalAmount) * toNumber(data.exchangeRate)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                      true,
                    ],
                  ]}
                />
              </div>
              <div className="sub-title">
                {data.orgCurrencySymbol || ""} {(toNumber(data.totalAmount) * toNumber(data.exchangeRate)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          )}
      </div>
    );
  };
  return (
    <div className="page-container">
      {/* Header */}
      <div className="invoice-header d-flex align-items-center justify-content-between mb-0">
        <div className="d-flex align-items-center gap-3">
          <img src={Back} alt="back" className="back-icon" onClick={() => navigate(-1)} style={{ cursor: "pointer", width: "24px", height: "24px" }} />

          {/* Logo Circle */}
          <div
            className="icon-circle"
            style={{
              background: "#B91C1C",
              color: "#FFFFFF",
              fontSize: "20px",
              fontWeight: "600",
            }}
          >
            R
          </div>

          <div>
            <div className="company-name">{invoiceData.companyName}</div>
            <div className="invoice-info">
              Invoice No. : <span className="text-link fw-500">{invoiceData.invoiceNo}</span>
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <div
            className="d-flex align-items-center pe-3"
            style={{ cursor: isPdfLoading ? "not-allowed" : "pointer" }}
            onClick={
              isPdfLoading
                ? undefined
                : showPdf
                ? () => setShowPdf(false)
                : handlePreviewPdf
            }
          >
            {isPdfLoading ? (
              <Spinner animation="border" size="sm" style={{ width: 18, height: 18 }} />
            ) : (
              <EyeIc />
            )}
            <span
              className="fs-13 fw-400 textblue ms-1"
              style={{ textDecoration: "underline", cursor: "inherit" }}
            >
              {showPdf ? "Back" : "Pdf view"}
            </span>
          </div>
          <div
            className="fs-14 fw-600 text-white d-flex justify-content-center align-items-center gap-2"
            style={{
              cursor: isSharing ? "not-allowed" : "pointer",
              padding: "0 16px",
              height: "36px",
              borderRadius: "100px",
              backgroundColor: "#25D366",
            }}
            onClick={isSharing ? undefined : handleShareWhatsapp}
            title="Share on WhatsApp"
          >
            {isSharing ? (
              <Spinner animation="border" size="sm" style={{ width: 16, height: 16 }} />
            ) : (
              <FaWhatsapp size={16} />
            )}
            Share
          </div>
          <Dropdown>
            <Dropdown.Toggle
              variant="success"
              size="sm"
              disabled={isPdfLoading}
              style={{
                background: "#10B981",
                border: "none",
                borderRadius: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontWeight: 600,
              }}
            >
              {isPdfLoading ? (
                <Spinner animation="border" size="sm" role="status" aria-hidden="true" style={{ width: "16px", height: "16px" }} />
              ) : (
                <img src={Download} alt="download" className="account-icon" style={{ filter: "brightness(0) invert(1)" }} />
              )}
              {isPdfLoading ? "Exporting..." : "Export"}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => handleDownloadPdf(1)}>Template 1</Dropdown.Item>
              <Dropdown.Item onClick={() => handleDownloadPdf(2)}>Template 2</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          {hasEditPermission && invoiceData.invoiceStatus === "Open" && (
            <Button
              onClick={() => navigate("/invoice/create", { state: { id } })}
              variant="light"
              size="sm"
              style={{
                background: "#FFFFFF",
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
                padding: "8px 16px",
                color: "#1E293B",
                fontSize: "14px",
              }}
            >
              Edit
            </Button>
          )}
          {hasEditPermission && !NON_CANCELLABLE_STATUSES.includes(invoiceData.invoiceStatus) && (
            <Button
              className={`cursor-pointer fs-14 fw-700 ${loadingStatus ? "disabled-btn" : ""} border-0 rounded-2 py-2 px-4 d-flex justify-content-center align-items-center`}
              onClick={!loadingStatus ? () => { setShowCancelConfirm(true); setCancelError(""); setCancelRemarks(""); } : () => {}}
              style={{
                minWidth: "100px",
                background: "#FF4444",
              }}
            >
              {loadingStatus === "Cancelled" ? <Spinner animation="border" size="sm" /> : "Cancel"}
            </Button>
          )}
          {invoiceData.is_delete && hasDeletePermission && (
            <div
              className="fs-14 fw-600 text-dkblue d-flex justify-content-center align-items-center"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteId(id);
              }}
              style={{
                cursor: "pointer",
                width: "36px",
                height: "36px",
                borderRadius: "100px",
                border: "1px solid #D4DDE7",
                background: "#FFFFFF",
              }}
              title="Delete Invoice"
            >
              <DeleteRow />
            </div>
          )}
        </div>
      </div>

      {showCancelConfirm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
          onClick={() => {
            if (!loadingStatus) {
              setShowCancelConfirm(false);
              setCancelRemarks("");
            }
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              width: "420px",
              maxWidth: "92vw",
              padding: "24px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h5 style={{ margin: 0, fontWeight: 700 }}>Cancel Invoice</h5>
            <p style={{ color: "#535E83", fontSize: "13px", marginTop: "8px" }}>
              Are you sure you want to cancel this invoice? This action cannot be undone.
            </p>
            {cancelError && (
              <div className="cancel-restriction-alert" role="alert" aria-live="assertive">
                <span className="alert-icon">⚠</span>
                <div className="alert-content">
                  <div className="alert-title">Cancellation Failed</div>
                  <div className="alert-message">{cancelError}</div>
                </div>
              </div>
            )}
            <label className="fs-13 fw-500" style={{ display: "block", marginTop: "12px", marginBottom: "6px", color: "#21272c" }}>
              Remarks
            </label>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Reason for cancellation (optional)"
              value={cancelRemarks}
              onChange={(e) => {
                setCancelRemarks(e.target.value);
              }}
              maxLength={1000}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "20px" }}>
              <button
                onClick={() => {
                  setShowCancelConfirm(false);
                  setCancelRemarks("");
                  setCancelError("");
                }}
                disabled={!!loadingStatus}
                style={{
                  background: "#fff",
                  color: "#21272c",
                  border: "1px solid #d4dde7",
                  borderRadius: "100px",
                  padding: "8px 24px",
                  fontWeight: 600,
                  fontSize: "14px",
                  cursor: loadingStatus ? "not-allowed" : "pointer",
                }}
              >
                Keep Invoice
              </button>
              <button
                onClick={handleCancelConfirm}
                disabled={!!loadingStatus}
                style={{
                  background: "#FF4444",
                  color: "#fff",
                  border: "none",
                  borderRadius: "100px",
                  padding: "8px 24px",
                  fontWeight: 600,
                  fontSize: "14px",
                  cursor: loadingStatus ? "not-allowed" : "pointer",
                  opacity: loadingStatus ? 0.7 : 1,
                }}
              >
                {loadingStatus === "Cancelled" ? <Spinner animation="border" size="sm" /> : "Cancel Invoice"}
              </button>
            </div>
          </div>
        </div>
      )}


      <div>
        {!showPdf  && (
          <div className="invoice-page">
            {/* Left Column - Narrower */}
            <div className="col-12 col-md-3">
              <div className="order-card">
                {/* Account Details */}
                <h5 className="card-title mb-3">Account details</h5>

                <div className="d-flex align-items-center mb-3" style={{ gap: "12px" }}>
                  <img src={Phone} alt="phone" className="account-icon" />
                  <div className="data-text">{invoiceData.phone}</div>
                </div>

                <div className="d-flex align-items-center mb-3" style={{ gap: "12px" }}>
                  <img src={Mail} alt="mail" className="account-icon" />
                  <div className="link-text">{invoiceData.email}</div>
                </div>

                <div className="d-flex align-items-center mb-3" style={{ gap: "12px" }}>
                  <img src={User} alt="user" className="account-icon" />
                  <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                    <span className="data-text">{invoiceData.ownerName}</span>
                    <span className="status-badge">Account owner</span>
                  </div>
                </div>

                <div className="d-flex align-items-center mb-3" style={{ gap: "12px" }}>
                  <img src={AccountType} alt="account type" className="account-icon" />
                  <div className="data-text">{invoiceData.customerIndustry}</div>
                </div>

                <div className="d-flex align-items-center mb-3" style={{ gap: "12px" }}>
                  <img src={Amount} alt="amount" className="account-icon" />
                  <div className="data-text">{invoiceData.creditAmount}</div>
                </div>

                <div className="d-flex align-items-center mb-4" style={{ gap: "12px" }}>
                  <img src={Location} alt="location" className="account-icon" />
                  <div className="data-text">{invoiceData.customerLocation}</div>
                </div>

                {/* <hr
                  style={{
                    margin: "20px 0",
                    border: "none",
                    borderTop: "1px solid #E2E8F0",
                  }}
                /> */}

                {/* <h5 className="card-title mb-3">Address</h5>
                <div className="data-text" style={{ marginBottom: "8px" }}>
                  {invoiceData.customerAddress}
                </div> */}

                {invoiceData.customerGstNo && (
                  <div className="d-flex align-items-center mb-3" style={{ gap: "8px" }}>
                    <span className="fs-13 text-grey">GST No:</span>
                    <span className="data-text">{invoiceData.customerGstNo}</span>
                  </div>
                )}

                <hr className="hr-divider" />

                <h5 className="card-title mb-3 d-flex align-items-center" style={{ gap: "8px" }}>
                  <img src={Location} alt="billing address" className="account-icon" />
                  Billing Address
                </h5>
                <div className="address-title">{invoiceData.billingAddressFull.name}</div>
                <div className="address-content mb-3">{invoiceData.billingAddressFull.address}</div>

                <h5 className="card-title mb-3 d-flex align-items-center" style={{ gap: "8px" }}>
                  <img src={Location} alt="shipping address" className="account-icon" />
                  Shipping Address
                </h5>
                <div className="address-title">{invoiceData.shippingAddress.name}</div>
                <div className="address-content">{invoiceData.shippingAddress.address}</div>

                {(invoiceData.salesPerson.name || invoiceData.salesPerson.phone || invoiceData.salesPerson.email) && (
                  <>
                    <hr className="hr-divider" />
                    <h5 className="card-title mb-3">Sales Person</h5>
                    <div className="d-flex align-items-center gap-3 mb-4">
                      <div className="icon-circle" style={{ background: "#EEF2FF", fontSize: "20px" }}>
                        {invoiceData.salesPerson.profile ? (
                          <img src={invoiceData.salesPerson.profile} alt="sales" className="sales-avatar" style={{ width: "40px", height: "40px", borderRadius: "50%", objectFit: "cover" }} />
                        ) : (
                          <img src={ProfileIc} alt="user" className="account-icon" style={{ width: "40px", height: "40px", borderRadius: "50%", objectFit: "cover" }} />
                        )}
                      </div>
                      <div>
                        {invoiceData.salesPerson.name && <div className="card-title">{invoiceData.salesPerson.name}</div>}
                        {invoiceData.salesPerson.phone && (
                          <div className="d-flex align-items-center gap-2 invoice-info">
                            <img src={Phone} alt="phone" className="account-icon" />
                            <span>{invoiceData.salesPerson.phone}</span>
                          </div>
                        )}
                        {invoiceData.salesPerson.email && (
                          <div className="d-flex align-items-center gap-2 invoice-info text-link">
                            <img src={Mail} alt="mail" className="account-icon" />
                            <span>{invoiceData.salesPerson.email}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                <hr className="hr-divider" />

                <h5 className="card-title mb-2">Created at</h5>
                <div className="secondary-text">{invoiceData.createdAt}{invoiceData.createdBy ? ` by` : ""}</div>
                {invoiceData.createdBy && <div className="data-text fw-500">{invoiceData.createdBy}</div>}
              </div>
            </div>

            {/* Right Column - Wider */}
            <div className="col-12 col-md-9 mt-4 mt-md-0">
              {!showPdf && (
        <div className="order-tabs-container">
          <div
            className={`order-tab ${activeTab === "Invoice Details" ? "active" : ""}`}
            onClick={() => setActiveTab("Invoice Details")}
          >
            Invoice Details
          </div>
          <div
            className={`order-tab ${activeTab === "Paid History" ? "active" : ""}`}
            onClick={() => setActiveTab("Paid History")}
          >
            Paid History
          </div>
          <div
            className={`order-tab ${activeTab === "Workflow" ? "active" : ""}`}
            onClick={() => setActiveTab("Workflow")}
          >
            Workflow
          </div>
        </div>
      )}

              {(activeTab === "Invoice Details") && (
                <div>
                  {/* Invoice Details */}
                  <div className="order-card mb-3 d-flex flex-wrap">
                    <div className="invoice-details-panel">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <h5 className="card-title mb-0">Invoice details</h5>
                    </div>

                <Row className="mb-3">
                  <Col xs={4}>
                    <div className="label-text">Sales Order</div>
                  </Col>
                  <Col xs={8}>
                    <div 
                      className="data-text textblue hover-underline-animation" 
                      style={{ textAlign: "left", cursor: "pointer", width: "fit-content" }}
                      onClick={() => {
                        if (invoiceData.orderId) {
                          navigate("/order/view", { state: { id: invoiceData.orderId } })
                        }
                      }}
                    >
                      {invoiceData.serialOrder}
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col xs={4}>
                    <div className="label-text">Invoice Number</div>
                  </Col>
                  <Col xs={8}>
                    <div className="link-text" style={{ textAlign: "left" }}>
                      {invoiceData.invoiceNumber}
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col xs={4}>
                    <div className="label-text">Invoice Date</div>
                  </Col>
                  <Col xs={8}>
                    <div className="data-text" style={{ textAlign: "left" }}>
                      {invoiceData.invoiceDate}
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col xs={4}>
                    <div className="label-text">Due Date</div>
                  </Col>
                  <Col xs={8}>
                    <div className="data-text" style={{ textAlign: "left" }}>
                      {invoiceData.dueDate}
                    </div>
                  </Col>
                </Row>

                {invoiceData.currencyName && (
                  <Row className="mb-3">
                    <Col xs={4}>
                      <div className="label-text">Currency</div>
                    </Col>
                    <Col xs={8}>
                      <div className="data-text" style={{ textAlign: "left" }}>
                        {currencySymbol} {invoiceData.currencyName}
                      </div>
                    </Col>
                  </Row>
                )}

                {invoiceData.exchangeRate !== "" && (
                  <Row className="mb-3">
                    <Col xs={4}>
                      <div className="label-text">Exchange Rate</div>
                    </Col>
                    <Col xs={8}>
                      <div className="data-text" style={{ textAlign: "left" }}>
                        {invoiceData.exchangeRate}
                      </div>
                    </Col>
                  </Row>
                )}

                <Row>
                  <Col xs={4}>
                    <div className="label-text">Invoice status</div>
                  </Col>
                  <Col xs={8}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        justifyContent: "start",
                      }}
                    >
                      {(() => {
                        const statusStyleMap = {
                          Paid: { color: "#10B981", icon: "✓" },
                          Cancelled: { color: "#FF4444", icon: "✕" },
                          Overdue: { color: "#FF4444", icon: "!" },
                          Due: { color: "#F59E0B", icon: "!" },
                          "Partially Paid": { color: "#F59E0B", icon: "✓" },
                        };
                        const { color, icon } = statusStyleMap[invoiceData.invoiceStatus] || { color: "#10B981", icon: "✓" };
                        return (
                          <span
                            style={{
                              color,
                              fontSize: "16px",
                              background: color,
                              borderRadius: "50%",
                              width: "18px",
                              height: "18px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <span style={{ color: "#FFFFFF", fontSize: "12px" }}>{icon}</span>
                          </span>
                        );
                      })()}
                      <span className="data-text">{invoiceData.invoiceStatus}</span>
                    </div>
                  </Col>
                </Row>
                  </div>

                  {/* Payment Summary */}
                  <div className="payment-summary-panel">
                    <h5 className="card-title mb-3">Payment Summary</h5>
                    <div className="payment-summary-grid">
                      <div className="payment-summary-item">
                        <div className="label-text">Total Amount</div>
                        <div className="data-text fw-600">{money(preTaxSubTotal)}</div>
                      </div>
                      <div className="payment-summary-item">
                        <div className="label-text">Discount</div>
                        <div className="data-text fw-600">{money(resolvedDiscount)}</div>
                      </div>
                      <div className="payment-summary-item">
                        <div className="label-text">Tax</div>
                        <div className="data-text fw-600">{money(totalGst)}</div>
                      </div>
                      
                      <div className="payment-summary-item">
                        <div className="label-text">Grand Total</div>
                        <div className="data-text fw-700">{money(toNumber(invoiceData.totalAmount))}</div>
                      </div>
                      {(toNumber(invoiceData.rawTdsAmount) > 0 || toNumber(invoiceData.rawTcsAmount) > 0) && (
                        <div className="payment-summary-item">
                          <div className="label-text">
                            {toNumber(invoiceData.rawTcsAmount) > 0 ? "TCS" : "TDS"}
                          </div>
                          <div className="data-text fw-600">
                            {toNumber(invoiceData.rawTcsAmount) > 0 ? " " : " "}
                            {money(
                              toNumber(invoiceData.rawTcsAmount) > 0
                                ? invoiceData.rawTcsAmount
                                : invoiceData.rawTdsAmount,
                            )}
                          </div>
                        </div>
                      )}
                      <div className="payment-summary-item">
                        <div className="label-text">Paid</div>
                        <div className="data-text fw-600" style={{ color: "#10B981" }}>{money(toNumber(invoiceData.paidAmount))}</div>
                      </div>
                      <div className="payment-summary-item">
                        <div className="label-text">Outstanding</div>
                        <div className="data-text fw-600" style={{ color: "#EF4444" }}>{money(toNumber(invoiceData.outstandingAmount))}</div>
                      </div>
                    </div>
                  </div>
              </div>
              {/* Organization Details */}
              {/* <div className="order-card mb-3" style={{ paddingTop: "12px", paddingBottom: "12px" }}>
                
 <div className="org-name fw-500 d-flex "><div className="card-title me-1">Organization Details: </div> {invoiceData.organizationDetails.name}</div>
                <Row className="align-items-center">
                  <Col md={8}>
                   

                    <div className="org-details-row">
                      {invoiceData.organizationDetails.gstNo && (
                        <div className="org-details-item">
                          <span className="label-text">GST No:</span>{" "}
                          <span className="data-text">{invoiceData.organizationDetails.gstNo}</span>
                        </div>
                      )}
                      <div className="org-details-item">
                        <span className="label-text">Account Name:</span>{" "}
                        <span className="data-text">{invoiceData.organizationDetails.accountName}</span>
                      </div>
                      <div className="org-details-item">
                        <span className="label-text">Bank Acc. No.:</span>{" "}
                        <span className="data-text">{invoiceData.organizationDetails.bankAccNo}</span>
                      </div>
                      <div className="org-details-item">
                        <span className="label-text">IFSC Code:</span>{" "}
                        <span className="data-text">{invoiceData.organizationDetails.ifscCode}</span>
                      </div>
                      <div className="org-details-item">
                        <span className="label-text">Swift Code:</span>{" "}
                        <span className="data-text">{invoiceData.organizationDetails.swiftCode}</span>
                      </div>
                      <div className="org-details-item">
                        <span className="label-text">UPI ID:</span>{" "}
                        <span className="data-text">{invoiceData.organizationDetails.upiId}</span>
                      </div>
                    </div>
                  </Col>

                  
                  {invoiceData.organizationDetails.upiIdQr && (
                    <Col md={4} className="d-flex flex-column align-items-center justify-content-center">
                      <div className="label-text mb-1">UPI QR</div>
                      <img
                        src={invoiceData.organizationDetails.upiIdQr}
                        alt="QR Code"
                        style={{
                          width: "64px",
                          height: "64px",
                          display: "block",
                        }}
                      />
                    </Col>
                  )}
                </Row>
              </div> */}

              {/* Products */}
              <div className="order-card mb-3">
                <h5 className="card-title mb-3">Products</h5>
                <div className="table-responsive">
                  {(() => {
                    const showCgst = invoiceData.products.some((p) => Number(p.cgst_amount) > 0);
                    const showSgst = invoiceData.products.some((p) => Number(p.sgst_amount) > 0);
                    const showIgst = invoiceData.products.some((p) => Number(p.igst_amount) > 0);
                    return (
                      <table className="items-table">
                        <thead style={{ background: "#F8FAFC" }}>
                          <tr>
                            
                            <th>HSN Code</th>
                            <th>Description</th>
                            <th>QTY</th>
                            <th>UOM</th>
                            <th>Unit Price</th>
                            <th>Amount</th>
                            {hasDiscount && <th>Discount</th>}
                            {showCgst && <th>CGST</th>}
                            {showSgst && <th>SGST</th>}
                            {showIgst && <th>IGST</th>}
                            <th>TOTAL</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoiceData.products.map((product, idx) => (
                            <tr key={idx}>
                              <td className="table-data">{product.hsnCode}</td>
                              <td className="table-data">{product.itemName}</td>
                              <td className="table-data">{product.quantity}</td>
                              <td className="table-data">{product.uom}</td>
                              <td className="table-data">{product.rate}</td>
                              <td className="table-data">{money(getItemBreakdown(product).baseAmount)}</td>
                              {hasDiscount && (
                                <td className="table-data">{money(getItemBreakdown(product).itemDiscount)}</td>
                              )}
                              {showCgst && (
                                <td className="table-data">
                                  {currencySymbol}{product.cgst_amount ?? 0} ({product.cgst_percentage ?? "-"})
                                </td>
                              )}
                              {showSgst && (
                                <td className="table-data">
                                  {currencySymbol}{product.sgst_amount ?? 0} ({product.sgst_percentage ?? "-"})
                                </td>
                              )}
                              {showIgst && (
                                <td className="table-data">
                                  {currencySymbol}{product.igst_amount ?? 0} ({product.igst_percentage ?? "-"})
                                </td>
                              )}
                              <td className="table-data">
                                {(() => {
                                  const bd = getItemBreakdown(product);
                                  const rows = [
                                    ["Amount", money(bd.baseAmount)],
                                    ...(bd.itemDiscount > 0
                                      ? [["Discount", `- ${money(bd.itemDiscount)}`]]
                                      : []),
                                    ["Taxable", money(bd.taxableAmount)],
                                    ...(bd.cgst > 0
                                      ? [[`CGST (${bd.cgst}%)`, `+ ${money(bd.cgstAmount)}`]]
                                      : []),
                                    ...(bd.sgst > 0
                                      ? [[`SGST (${bd.sgst}%)`, `+ ${money(bd.sgstAmount)}`]]
                                      : []),
                                    ...(bd.igst > 0
                                      ? [[`IGST (${bd.igst}%)`, `+ ${money(bd.igstAmount)}`]]
                                      : []),
                                    ["Total", money(bd.total), true],
                                  ];
                                  return (
                                    <span className="d-inline-flex align-items-center gap-1">
                                      {money(bd.total)}
                                      <InfoTooltip rows={rows} />
                                    </span>
                                  );
                                })()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>

                {/* Totals */}
                <div className="d-flex justify-content-end mt-3">
                  <SubTotals data={invoiceData ?? {}} />
                </div>
              </div>

              
              {/* Terms & Customer Notes */}
              <div className="order-card mb-3">
                <h6 className="card-title">Terms & Conditions</h6>
                <p className="invoice-info" style={{ marginBottom: "16px" }}>
                  {invoiceData.termsConditions}
                </p>
                <h6 className="card-title">Customer Notes</h6>
                <p className="invoice-info" style={{ marginBottom: "0" }}>
                  {invoiceData.customerNotes}
                </p>
              </div>
              </div>
            )}
             {activeTab === "Paid History" && (
          <div className="invoice-page">
            <div className="col-12">
              <div className="order-card">
                <h5 className="card-title mb-3">Paid History</h5>

                {isHistoryLoading ? (
                  <div className="d-flex justify-content-center py-4">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : paidHistory.length === 0 ? (
                  <p className="text-center invoice-info py-4">No payment history available</p>
                ) : (
                  <div className="d-flex flex-column ms-3">
                    {paidHistory.map((txn, idx) => (
                      <div key={txn.id ?? idx} className="d-flex mb-4 position-relative">
                        {/* Green tick */}
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle"
                          style={{
                            backgroundColor: "#E6FFF1",
                            width: "40px",
                            height: "40px",
                            marginRight: "15px",
                            marginLeft: "-19px",
                            zIndex: 1,
                          }}
                        >
                          <div
                            className="d-flex align-items-center justify-content-center rounded-circle"
                            style={{
                              backgroundColor: "#4EBD7E",
                              width: "20px",
                              height: "20px",
                              color: "white",
                              fontSize: "14px",
                              fontWeight: "bold",
                            }}
                          >
                            ✓
                          </div>
                        </div>

                        {/* Vertical dashed line */}
                        {idx !== paidHistory.length - 1 && (
                          <div
                            className="position-absolute start-0"
                            style={{
                              borderLeft: "2px dashed #3469E7",
                              height: "calc(100% + 16px)",
                              left: "8px",
                              top: "10px",
                            }}
                          />
                        )}

                        {/* Payment details */}
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center mb-2 flex-wrap">
                            <span className="fw-700 fs-16 me-2" style={{ color: "#2C8C53" }}>
                              +{money(txn.amount ?? 0)}
                            </span>
                            <div className="backgroundGreyDot me-2" style={{ width: "4px", height: "4px" }}></div>
                            <span
                              className="badge rounded-pill fs-11 fw-600"
                              style={{ backgroundColor: "#4F46E5" }}
                            >
                              {sanitizeInput(txn.payment_method ?? "-")}
                            </span>
                            {(txn.is_delete === true || txn.is_delete === 1 || Boolean(txn.is_delete)) && hasDeletePermission && (
                              <span
                                onClick={() => {
                                  setHistoryDeleteId(txn.id);
                                  setIsOpenHistoryDelete(true);
                                }}
                                title="Delete this payment"
                                style={{
                                  marginLeft: "10px",
                                  cursor: "pointer",
                                  width: "26px",
                                  height: "26px",
                                  borderRadius: "100px",
                                  border: "1px solid #E5E5E5",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <FaTrash size={11} color="#FF4444" />
                              </span>
                            )}
                          </div>
                          {(txn.payment_code || txn.invoice_code) && (
                            <p className="m-0 fs-13 fw-500 mb-1" style={{ color: "#1F2937" }}>
                              {sanitizeInput(txn.payment_code ?? "-")}
                              {txn.invoice_code ? ` · ${sanitizeInput(txn.invoice_code)}` : ""}
                            </p>
                          )}
                          {txn.remarks && (
                            <p className="m-0 fs-14 fw-500 mb-2" style={{ color: "#1F2937", lineHeight: "1.4" }}>
                              {sanitizeInput(txn.remarks)}
                            </p>
                          )}
                          <small className="invoice-info d-block mb-1">
                            {sanitizeInput(txn.date ?? "-")}
                            {txn.time ? ` at ${sanitizeInput(txn.time)}` : ""}
                            {txn.created_by ? ` by ${sanitizeInput(txn.created_by)}` : ""}
                          </small>
                          {invoiceData.organizationCurrencyId &&
                            invoiceData.customerCurrencyId &&
                            invoiceData.organizationCurrencyId !== invoiceData.customerCurrencyId &&
                            txn.exchange_rate && (
                              <small className="invoice-info d-block mb-1">
                                Rate: × {toNumber(txn.exchange_rate)} ={" "}
                                {invoiceData.orgCurrencySymbol || ""}{" "}
                                {(txn.converted_amount != null
                                  ? toNumber(txn.converted_amount)
                                  : toNumber(txn.amount) * toNumber(txn.exchange_rate)
                                ).toLocaleString("en-IN", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </small>
                            )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "Workflow" &&
          (() => {
            let formattedDate = "";
            if (workflowList.length > 0 && workflowList[0].date) {
              const dateObj = new Date(workflowList[0].date);
              formattedDate = dateObj.toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              });
            }
            return (
              <div className="invoice-page">
                <div className="col-12">
                  <div className="order-card p-4">
                    <div className="card-title mb-1">Workflow</div>
                    {isWorkflowLoading ? (
                      <div className="d-flex justify-content-center py-4">
                        <Loader height="150px" />
                      </div>
                    ) : (
                      <div className="py-4 custom-scrollbar" style={{ overflowX: "hidden", maxHeight: "calc(100vh - 220px)", overflowY: "auto", paddingRight: "8px", paddingBottom: "24px" }}>
                        <div>
                          <div style={{ minWidth: "300px" }}>
                            {formattedDate && (
                              <div className="position-relative text-center mb-4">
                                <hr />
                                <span
                                  className="position-absolute top-50 start-50 translate-middle bg-white px-4 py-1 text-muted small border rounded-pill"
                                  style={{ fontWeight: 500, fontSize: "15px" }}
                                >
                                  {formattedDate}
                                </span>
                              </div>
                            )}

                            {workflowList && workflowList.length > 0 ? (
                              workflowList.map((workflowItem, index) => {
                                const statusKey = workflowItem.status?.toLowerCase() || "";
                                const Icon = workflowIcons[statusKey] || workflowIcons["pending"];

                                return (
                                  <div
                                    key={index}
                                    className="d-flex align-items-stretch"
                                    style={{ marginBottom: "24px" }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "start",
                                        marginRight: "1rem",
                                        alignSelf: "stretch",
                                      }}
                                    >
                                      <div
                                        className="small text-muted mb-1"
                                        style={{ marginRight: "1rem", minWidth: "60px" }}
                                      >
                                        {workflowItem.time}
                                      </div>

                                      <div
                                        style={{
                                          position: "relative",
                                          display: "flex",
                                          flexDirection: "column",
                                          alignItems: "center",
                                          alignSelf: "stretch",
                                        }}
                                      >
                                        <div
                                          style={{
                                            backgroundColor: "#f8f9fa",
                                            border: "1px solid #dee2e6",
                                            borderRadius: "50%",
                                            width: "30px",
                                            height: "30px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            zIndex: 1,
                                          }}
                                        >
                                          {Icon}
                                        </div>

                                        {index !== workflowList.length - 1 && (
                                          <div
                                            style={{
                                              flex: 1,
                                              width: "0px",
                                              borderLeft: "1.5px dashed #D3D8E0",
                                              marginTop: "4px",
                                              marginBottom: "-24px",
                                            }}
                                          />
                                        )}
                                      </div>
                                    </div>

                                    <div style={{ flex: 1 }}>
                                      <p className="mb-1">
                                        <strong style={{ fontWeight: 500, fontSize: "16px", color: "#21272C" }}>
                                          {workflowItem.status}
                                        </strong>
                                      </p>
                                      {workflowItem.description && (
                                        <div className="small mb-1" style={{ color: "#4B5563" }}>
                                          {workflowItem.description}
                                        </div>
                                      )}
                                      <div className="small text-muted">
                                        By {workflowItem.first_name || "System"} on {workflowItem.date}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div style={{ textAlign: "center", padding: "40px", color: "#999", fontSize: "14px" }}>
                                No workflow found
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
          </div>)}




        {isOpenHistoryDelete && (
          <DeleteModal
            deleteUrl={`invoice_payment_history/delete/${historyDeleteId}`}
            onSuccess={onSuccessHistoryDelete}
            onCancel={cancelHistoryDelete}
          />
        )}

        {showPdf && (
          <div
            style={{
              position: "relative",
              width: "100%",
              height: "calc(95vh - 84px)",
            }}
          >
            {/* Close Icon */}
            <div
              onClick={() => setShowPdf(false)}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                zIndex: 9999,
                background: "#fff",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}
            >
              ✕
            </div>

            {pdfUrl ? (
              <iframe
                src={pdfUrl}
                style={{ width: "100%", height: "100%", border: "none" }}
                title="Invoice PDF Viewer"
              />
            ) : (
              <div className="data-text">PDF preview not available.</div>
            )}
          </div>
        )}
      </div>

      {isOpenDelete && (
        <DeleteModal
          deleteUrl={`invoice/delete/${deleteId}`}
          onSuccess={onSuccessDelete}
          onCancel={cancelDelete}
        />
      )}
      {isOpenHistoryDelete && (
        <DeleteModal
          deleteUrl={`invoice_payment_history/delete/${historyDeleteId}`}
          onSuccess={onSuccessHistoryDelete}
          onCancel={cancelHistoryDelete}
        />
      )}

      <ToastContainer
        position="top-end"
        style={{
          position: "fixed",
          top: "24px",
          right: "24px",
          zIndex: 99999,
        }}
      >
        <Toast
          className="d-inline-block m-1 shadow-lg border-0"
          style={{
            minWidth: "340px",
            maxWidth: "480px",
            borderRadius: "10px",
            backgroundColor: snackbarStatus === true ? "#10B981" : "#EF4444",
          }}
          show={showSnackbar}
          onClose={handleSnackbarClose}
          delay={5000}
          autohide
        >
          <Toast.Body
            style={{
              color: "#ffffff",
              fontSize: "14px",
              fontWeight: 500,
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span>{snackbarStatus === true ? "✓" : "⚠"}</span>
            <span>{snackbarMessage}</span>
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default InvoiceView;
